// @ts-strict-ignore
import * as React from 'react';
import Layout from 'antd/lib/layout/layout';
import { ReleaseStepOrStepRunComponentProps } from '@/components/ValidatedRouteComponents';
import { CalculatedDataPerSection, MonitoringSectionFilter } from './../monitoring/MonitoringContainer';
import { useDispatch, useSelector } from 'react-redux';
import { trackReleaseRouteEvent } from '@/actions/trackEvent';
import * as AnalyticEvents from '@/utils/analytics';
import { RootState } from '@/reducers/reducers';
import {
    selectIsReleaseFromScraper,
    selectMonitoringMetricsById,
    selectIsDemoInstanceOrDemoModeEnabled,
    selectStepConfigsByType,
    selectStepRun,
    selectFeatureFlaggingIntegration,
    selectStabilityIntegrations,
} from '@/selectors/selectors';
import {
    AppMonitoringHealthMetricCategory,
    AppPlatform,
    AppStoreType,
    AutomationType,
    FeatureFlag,
    ReleaseStepType,
    RolloutStepRunData,
    RolloutMonitoring,
    SettingType,
    IntegrationType,
    IntegrationLaunchDarkly,
    IntegrationOptimizely,
    IntegrationStatsig,
    ExpoEASRolloutData,
} from '@/types';
import FeatureFlagDrawer from '@/components/featureFlags/FeatureFlagDrawer';
import { iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { RolloutStepSettings } from '@/types/stepConfig';
import {
    massageMonitoringData,
    monitoringStatisticsForStepInstallations,
    getAutomationIdByType,
    isExpoEASRolloutDestination,
    hasSupportForPhasedRelease,
} from '@/helpers';
import { runwayColors } from '../styles/globalStyles';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import { createAppSettingsRouteWithSettingType } from '@/helpers/routeHelpers';
import { usePhasedReleaseState } from '@/utils/hooks/usePhasedReleaseState';
import { RolloutMonitoringSummaryContent } from './../monitoring/MonitoringSummaryContent';
import { ReleaseEventsTimelineSection } from '../releaseOverview/ReleaseTimelineSection';
import { ReleaseTimelineSectionContext } from '../timelineEvents/EventsTimeline';
import { ExpoEASRolloutStep } from './ExpoEASRolloutStep';

enum RolloutHeaderState {
    WaitingForRelease = 'waitingForRelease',
    Superseded = 'superseded',
    NotStarted = 'notStarted',
    Paused = 'paused',
    InProgress = 'inProgress',
    Complete = 'complete',
}

const titleForRolloutState = (state: RolloutHeaderState) => {
    switch (state) {
        case RolloutHeaderState.WaitingForRelease:
            return 'Waiting to start rollout';
        case RolloutHeaderState.Superseded:
            return 'Superseded';
        case RolloutHeaderState.NotStarted:
            return 'Ready to start rollout';
        case RolloutHeaderState.Paused:
            return 'Rollout paused';
        case RolloutHeaderState.InProgress:
            return 'Rollout in progress';
        case RolloutHeaderState.Complete:
            return 'Rollout complete';
    }
};

const colorForRolloutState = (state: RolloutHeaderState) => {
    switch (state) {
        case RolloutHeaderState.WaitingForRelease:
            return runwayColors.statusGrey;
        case RolloutHeaderState.Paused:
            return runwayColors.statusRed;
        case RolloutHeaderState.NotStarted:
        case RolloutHeaderState.InProgress:
            return runwayColors.statusYellow;
        case RolloutHeaderState.Superseded:
        case RolloutHeaderState.Complete:
            return runwayColors.statusGreen;
    }
};

const descriptionForRolloutState = (
    state: RolloutHeaderState,
    appStoreType: AppStoreType,
    isFullReleaseWithoutPhasedRollout: boolean,
) => {
    const rolloutText = appStoreType === AppStoreType.Apple ? 'phased release' : 'staged rollout';

    switch (state) {
        case RolloutHeaderState.WaitingForRelease:
            return 'Your app update has not been released yet.';
        case RolloutHeaderState.Superseded:
            return `Your ${rolloutText} has been superseded by a newer release.`;
        case RolloutHeaderState.NotStarted:
            return `Start your ${rolloutText} to begin the automated rollout sequence.`;
        case RolloutHeaderState.Paused:
            return `Your ${rolloutText} has been halted.`;
        case RolloutHeaderState.InProgress:
            return `Your app update is being released via ${rolloutText}.`;
        case RolloutHeaderState.Complete:
            if (isFullReleaseWithoutPhasedRollout) {
                return 'Your app update has been released.';
            }
            return `Your ${rolloutText} is complete.`;
    }
};

export const StepContentRollout: React.FC<ReleaseStepOrStepRunComponentProps> = (props) => {
    const stepRun = useSelector((state: RootState) => selectStepRun(state, props.stepRunComponentProps?.stepRunId));
    const isExpoEASDestination = isExpoEASRolloutDestination(stepRun.destinations ?? []);
    if (!isExpoEASDestination) {
        return <NativeStepContentRollout {...props} />;
    }
    return <ExpoStepContentRollout {...props} />;
};

const ExpoStepContentRollout: React.FC<ReleaseStepOrStepRunComponentProps> = (props) => {
    const stepRun = useSelector((state: RootState) => selectStepRun(state, props.stepRunComponentProps?.stepRunId));
    const stepData: RolloutStepRunData = { ...stepRun.data.summaryData, ...stepRun.data.complementaryData };

    return <NativeStepContentRollout {...props} isExpoEAS expoEASRolloutData={stepData.expoEasRollout} />;
};

interface NativeStepContentRolloutProps extends ReleaseStepOrStepRunComponentProps {
    isExpoEAS?: boolean;
    expoEASRolloutData?: ExpoEASRolloutData;
}

const NativeStepContentRollout: React.FC<NativeStepContentRolloutProps> = ({
    orgId,
    appId,
    releaseId,
    releaseStepType,
    stepRunComponentProps,
    isExpoEAS = false,
    expoEASRolloutData,
}) => {
    const dispatch = useDispatch();
    const stepRunId = stepRunComponentProps?.stepRunId;
    const isDemoMode = useSelector((state: RootState) =>
        selectIsDemoInstanceOrDemoModeEnabled(state, orgId, releaseId),
    );
    const isReleaseFromScraper = useSelector((state: RootState) => selectIsReleaseFromScraper(state, releaseId)); // TODO-WF: read from step run data when scraper logic is migrated to WFs
    const stepRun = useSelector((state: RootState) => selectStepRun(state, stepRunId));
    const stepData: RolloutStepRunData = { ...stepRun.data.summaryData, ...stepRun.data.complementaryData };
    const isReactNativeOTADestination = stepData.platform === AppPlatform.ReactNativeOTA;
    const metricsById = useSelector((state: RootState) => selectMonitoringMetricsById(state, appId));
    // Live rollout step config, never stepRun.stepConfigSnapshot: snapshots are stamped at run
    // creation and drift to installations that no longer exist, which drops every stat (RUN-18353).
    const rolloutStepConfigs = useSelector((state: RootState) =>
        selectStepConfigsByType(state, appId, ReleaseStepType.Rollout),
    );
    const stepInstallationIds = rolloutStepConfigs[stepRun.stepConfigId]?.integrationInstallationIds;

    const rolloutMonitoring: RolloutMonitoring = {
        automationRuns: stepRun.automationRuns,
        releaseAdoptions: stepData.releaseAdoptions,
        monitoringStatistics: monitoringStatisticsForStepInstallations(
            stepData.monitoringStatistics,
            stepInstallationIds,
        ),
        featureFlagTimeSeries: stepData.featureFlagTimeSeries,
        monitoringAutomations: stepData.monitoringAutomations,
        checklistItems: stepRun.checklistItems,
        appStoreSelectedBuilds: stepData.appStoreSelectedBuilds,
        appStoreReviewData: stepData.appStoreReviewData,
        rolloutStartedAt: stepData.rolloutStartedAt,
    };

    const autoHaltUnstablePhasedReleasesAutomationId = getAutomationIdByType(
        stepRun.automationRuns,
        AutomationType.AutoHaltUnstablePhasedReleases,
    );
    const autoPromoteStablePhasedReleasesAutomationId = getAutomationIdByType(
        stepRun.automationRuns,
        AutomationType.AutoPromoteStablePhasedReleases,
    );
    const autoStagedRolloutAutomationId = getAutomationIdByType(
        stepRun.automationRuns,
        AutomationType.AutoStagedRollout,
    );
    const rolloutSettings = stepRun.stepConfigSnapshot?.settings as RolloutStepSettings;
    const sourceStepConfigId = rolloutSettings?.sourceStepConfigId;
    const sourceStepArchetype =
        sourceStepConfigId ? rolloutSettings?.sourceStepConfigOptions?.[sourceStepConfigId]?.archetype : null;
    const isRolloutSourcedFromBetaStep = sourceStepArchetype === ReleaseStepType.BetaTesting;
    const displayWaitingForRelease = !isRolloutSourcedFromBetaStep && !stepData.releasedAt;
    const appStoreAvailableBuilds = rolloutMonitoring?.appStoreSelectedBuilds ?? [];
    const rolloutStartedAt = rolloutMonitoring?.rolloutStartedAt;
    const {
        hasPhasedReleaseState,
        isPhasedRollout: isAutomatedPhasedRollout,
        rolloutComplete: phasedRolloutComplete,
        phasedReleasePaused,
        isSuperseded,
        needsAutomatedRolloutStart,
    } = usePhasedReleaseState({
        appId,
        appStoreType: stepData.appStoreType,
        phasedRelease: stepData.phasedRelease,
        autoStagedRolloutAutomationId,
        rolloutStartedAt,
    });
    const isFullReleaseWithoutPhasedRollout =
        isReactNativeOTADestination || (!hasSupportForPhasedRelease(stepData.appStoreType) && !!stepData.releasedAt);
    const rolloutComplete = isFullReleaseWithoutPhasedRollout || phasedRolloutComplete;
    const rolloutState =
        displayWaitingForRelease ? RolloutHeaderState.WaitingForRelease
        : isSuperseded ? RolloutHeaderState.Superseded
        : needsAutomatedRolloutStart ? RolloutHeaderState.NotStarted
        : phasedReleasePaused ? RolloutHeaderState.Paused
        : rolloutComplete ? RolloutHeaderState.Complete
        : RolloutHeaderState.InProgress;
    const showLoading = !rolloutMonitoring;
    const monitoringSettingsLink = createAppSettingsRouteWithSettingType(orgId, appId, SettingType.HealthMetrics);
    const appStoreReviewData = rolloutMonitoring?.appStoreReviewData;
    const featureFlags = rolloutMonitoring?.featureFlagTimeSeries ?? [];

    const featureFlaggingIntegration = useSelector((state: RootState) =>
        selectFeatureFlaggingIntegration(state, appId),
    );
    const stabilityIntegrations = useSelector((state: RootState) => selectStabilityIntegrations(state, appId));
    const [featureFlaggingIntegrationIcon, featureFlaggingIntegrationIconAlt] =
        featureFlaggingIntegration ? iconAndAltForIntegrationType(featureFlaggingIntegration.id) : [null, null];

    const [selectedFeatureFlag, setSelectedFeatureFlag] = React.useState<FeatureFlag | null>(null);
    const [selectedBuild, setSelectedBuild] = React.useState(null);
    const [selectedSection, setSelectedSection] = React.useState<MonitoringSectionFilter>(MonitoringSectionFilter.All);

    const onShowFeatureFlag = React.useCallback((flag: FeatureFlag) => {
        setSelectedFeatureFlag(flag);
    }, []);

    const featureFlagIntegrationEnvironment = (() => {
        switch (featureFlaggingIntegration?.id) {
            case IntegrationType.Optimizely:
                return (featureFlaggingIntegration as IntegrationOptimizely).data?.selectedEnvironment?.name;
            case IntegrationType.LaunchDarkly:
                return (featureFlaggingIntegration as IntegrationLaunchDarkly).data?.selectedEnvironment?.name;
            case IntegrationType.Statsig:
                return (featureFlaggingIntegration as IntegrationStatsig).data?.selectedEnvironment?.name;
            default:
                return null;
        }
    })();

    const featureFlagIntegrationProject = (() => {
        switch (featureFlaggingIntegration?.id) {
            case IntegrationType.Optimizely:
                return (featureFlaggingIntegration as IntegrationOptimizely).data?.selectedProject?.name;
            case IntegrationType.LaunchDarkly:
                return (featureFlaggingIntegration as IntegrationLaunchDarkly).data?.selectedProject?.name;
            case IntegrationType.Statsig: {
                return (featureFlaggingIntegration as IntegrationStatsig).data?.selectedProject?.name;
            }
            default:
                return null;
        }
    })();

    const handleInspectFeatureFlag = React.useCallback(
        (chartName: string) => {
            dispatch(
                trackReleaseRouteEvent({
                    orgId,
                    appId,
                    releaseId,
                    eventName: AnalyticEvents.Event.InspectFeatureFlagOnRolloutChart,
                    chartName,
                }),
            );
        },
        [dispatch, orgId, appId, releaseId],
    );

    const handleMonitoringSectionFilterChange = (value: string) => {
        setSelectedSection(value as MonitoringSectionFilter);
    };

    React.useEffect(() => {
        dispatch(
            trackReleaseRouteEvent({
                orgId: orgId,
                appId: appId,
                releaseId: releaseId,
                eventName: AnalyticEvents.Event.ViewRolloutMonitoring,
            }),
        );
    }, []);

    React.useEffect(() => {
        if (appStoreAvailableBuilds.length > 0) {
            setSelectedBuild(appStoreAvailableBuilds[0]);
        }
    }, [stepRunComponentProps.stepRunId]);

    let groupedMonitoringStatisticsForCards = massageMonitoringData({
        monitoringStatistics: rolloutMonitoring?.monitoringStatistics ?? [],
        monitoringAutomations: rolloutMonitoring?.monitoringAutomations ?? [],
        metricsById,
        stepInstallationIds,
    });

    let groupedMonitoringStatistics = massageMonitoringData({
        monitoringStatistics: rolloutMonitoring?.monitoringStatistics ?? [],
        monitoringAutomations: rolloutMonitoring?.monitoringAutomations ?? [],
        metricsById,
        shouldGroupCrashFreeStats: false,
        stepInstallationIds,
    });

    const isMultiApk = appStoreAvailableBuilds?.length > 1;

    if (isMultiApk) {
        groupedMonitoringStatisticsForCards = groupedMonitoringStatisticsForCards.filter((stat) => {
            if (stat.category !== AppMonitoringHealthMetricCategory.Stability) {
                return true;
            }

            return stat.buildIdentifier === selectedBuild?.appStoreBuild.buildNumber;
        });

        groupedMonitoringStatistics = groupedMonitoringStatistics.filter((stat) => {
            if (stat.category !== AppMonitoringHealthMetricCategory.Stability) {
                return true;
            }

            return stat.buildIdentifier === selectedBuild?.appStoreBuild.buildNumber;
        });
    }

    const calculatedDataPerSection: CalculatedDataPerSection = groupedMonitoringStatistics.reduce(
        (acc, groupedMonitoringStatistic) => {
            const data = acc[groupedMonitoringStatistic.category] ?? {
                shouldShowAlert: false,
                groupedStatistics: [],
            };

            data.groupedStatistics.push(groupedMonitoringStatistic);
            acc[groupedMonitoringStatistic.category] = data;
            return acc;
        },
        {},
    );

    return (
        <>
            <Layout style={{ paddingTop: 15 }}>
                <RolloutMonitoringSummaryContent
                    headerSection={
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 30 }}>
                            <Space direction="vertical" size={4}>
                                <Typography.Text
                                    style={{
                                        fontSize: 30,
                                        color: colorForRolloutState(rolloutState),
                                        fontWeight: 600,
                                    }}
                                >
                                    {titleForRolloutState(rolloutState)}
                                </Typography.Text>
                                <Typography.Text type="secondary">
                                    {descriptionForRolloutState(
                                        rolloutState,
                                        stepData.appStoreType,
                                        isFullReleaseWithoutPhasedRollout,
                                    )}
                                </Typography.Text>
                            </Space>
                            <ReleaseEventsTimelineSection
                                orgId={orgId}
                                appId={appId}
                                releaseId={releaseId}
                                stepRunId={stepRunComponentProps?.stepRunId}
                                context={ReleaseTimelineSectionContext.ReleaseStep}
                                releaseStepType={releaseStepType}
                            />
                        </div>
                    }
                    orgId={orgId}
                    appId={appId}
                    releaseId={releaseId}
                    stepRunComponentProps={stepRunComponentProps}
                    isReleaseFromScraper={isReleaseFromScraper}
                    displayWaitingForRelease={displayWaitingForRelease}
                    calculatedDataPerSection={calculatedDataPerSection}
                    releaseVersion={stepData.internalVersionString}
                    onShowFeatureFlag={onShowFeatureFlag}
                    onInspectFeatureFlag={handleInspectFeatureFlag}
                    setSelectedBuild={setSelectedBuild}
                    handleMonitoringSectionFilterChange={handleMonitoringSectionFilterChange}
                    platform={stepData.platform}
                    isDemoMode={isDemoMode}
                    isAutomatedPhasedRollout={isAutomatedPhasedRollout}
                    phasedRelease={stepData.phasedRelease}
                    appStoreType={stepData.appStoreType}
                    appStoreIntegrationId={stepData.appStoreIntegrationType}
                    rolloutMonitoring={rolloutMonitoring}
                    groupedMonitoringStatisticsForCards={groupedMonitoringStatisticsForCards}
                    isRolloutSourcedFromBetaStep={isRolloutSourcedFromBetaStep}
                    rolloutStartedAt={rolloutStartedAt}
                    isPhased={hasPhasedReleaseState}
                    isMultiApk={isMultiApk}
                    autoHaltUnstablePhasedReleasesAutomationId={autoHaltUnstablePhasedReleasesAutomationId}
                    autoPromoteStablePhasedReleasesAutomationId={autoPromoteStablePhasedReleasesAutomationId}
                    autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                    selectedBuild={selectedBuild}
                    isLoading={showLoading}
                    section={selectedSection}
                    monitoringSettingsLink={monitoringSettingsLink}
                    releasedAt={stepData.releasedAt}
                    appStoreSelectedBuilds={appStoreAvailableBuilds}
                    stabilityIntegrations={stabilityIntegrations}
                    shouldShowDisabledUI={null}
                    appStoreReviewData={appStoreReviewData}
                    featureFlags={featureFlags}
                    automationRuns={null}
                    platformRolloutStatus={
                        isExpoEAS ?
                            <ExpoEASRolloutStep
                                stepRunComponentProps={stepRunComponentProps}
                                data={expoEASRolloutData}
                            />
                        :   undefined
                    }
                    hideRolloutStatus={isReactNativeOTADestination}
                    hideRolloutActions={isExpoEAS}
                    hideMonitoringAutomations={isExpoEAS || isReactNativeOTADestination}
                    hideReleaseFeedbackPrompt={isExpoEAS}
                />
            </Layout>
            {featureFlaggingIntegration && !!selectedFeatureFlag && (
                <FeatureFlagDrawer
                    orgId={orgId}
                    appId={appId}
                    releaseId={releaseId}
                    currentVersion={stepData.internalVersionString}
                    featureFlag={selectedFeatureFlag}
                    open={!!selectedFeatureFlag}
                    onClose={() => setSelectedFeatureFlag(null)}
                    integrationId={featureFlaggingIntegration.id}
                    integrationName={featureFlaggingIntegration.name}
                    projectName={featureFlagIntegrationProject}
                    environmentName={featureFlagIntegrationEnvironment}
                    integrationIcon={featureFlaggingIntegrationIcon}
                    integrationIconAlt={featureFlaggingIntegrationIconAlt}
                />
            )}
        </>
    );
};
